import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import {
  createModelVersion,
  deleteModelVersion,
  editModelVersion,
} from 'src/hooks/useModelVersions';
import { useNavigate } from 'react-router-dom';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export const ModelVersionModal = ({
  applicationId,
  modalMode,
  values,
  onClose,
  visible,
  redirectLocation,
}: {
  applicationId: string;
  modalMode: 'New' | 'Edit' | 'Delete';
  values?: {
    id?: string;
    name: string;
    description?: string;
    version?: number;
  };
  onClose: () => void;
  visible: boolean;
  redirectLocation?: string;
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const ModelVersionValidationSchema = yup.object().shape({
    name: yup.string().required('Please specify a model version name.'),
    description: yup.string(),
  });

  const initialValues = {
    name: values?.name || '',
    description: values?.description || '',
  };

  useEffect(() => {
    formik.setValues({
      name: values?.name || '',
      description: values?.description || '',
    });
    formik.setTouched({});
  }, [values]);

  const [error, setError] = useState<Error | null>(null);

  const formik = useFormik({
    initialValues,
    onSubmit: async (formValues) => {
      let response;
      // check the mode, call the correct api
      setLoading(true);
      switch (modalMode) {
        case 'New':
          response = await createModelVersion(
            {
              applicationId,
              name: formValues.name,
              description: formValues.description,
            },
            queryClient,
          )
            .catch((e) => {
              setError(e);
              return e.message;
            })
            .finally(() => {
              setLoading(false);
            });
          break;
        case 'Edit':
          if (!values?.id) return;
          response = await editModelVersion(
            {
              id: values?.id,
              applicationId,
              name: formValues.name,
              description: formValues.description,
              version: (values?.version || 1) + 1,
            },
            queryClient,
          )
            .catch((e) => {
              setError(e);
              return e.message;
            })
            .finally(() => {
              setLoading(false);
            });
          break;
      }

      if (!response) onClose();
    },
    validationSchema: ModelVersionValidationSchema,
  });

  const handleDeleteConfirm = async () => {
    if (!values?.id) return;
    setLoading(true);
    const response = await deleteModelVersion(
      {
        id: values?.id,
        applicationId,
      },
      queryClient,
    )
      .catch((e) => {
        setError(e);
        return e.message;
      })
      .finally(() => {
        setLoading(false);
        if (redirectLocation) {
          navigate(redirectLocation);
        }
      });

    if (!response) onClose();
  };

  if (!visible) return <></>;

  return (
    <Modal
      visible={true}
      onDismiss={onClose}
      header={`${modalMode} Model Version`}
      footer={
        <Box float="right">
          <SpaceBetween size="m" direction="horizontal">
            <Button variant="link" onClick={onClose}>
              Cancel
            </Button>
            <PermissionAwareButton
              requiredPermission="ADMINISTER"
              actionDescription={`${modalMode === 'Delete' ? 'delete' : 'create or update'} model versions`}
              loading={loading}
              variant={'primary'}
              formAction={modalMode !== 'Delete' ? 'submit' : undefined}
              onClick={() => {
                modalMode === 'Delete'
                  ? handleDeleteConfirm()
                  : formik.handleSubmit();
              }}
            >
              {modalMode === 'Delete' ? 'Delete Model Version' : 'Submit'}
            </PermissionAwareButton>
          </SpaceBetween>
        </Box>
      }
    >
      {modalMode === 'Delete' ? (
        <p>This will delete the Model Version. Confirm?</p>
      ) : (
        <div className="modal-form">
          <form onSubmit={formik.handleSubmit}>
            <SpaceBetween size="l">
              <FormField
                label="Model Version Name"
                errorText={formik.touched.name && formik.errors.name}
              >
                <Input
                  value={formik.values.name}
                  placeholder="My Model Version"
                  onChange={(e) => formik.setFieldValue('name', e.detail.value)}
                  onBlur={() =>
                    formik.setTouched({ ...formik.touched, name: true })
                  }
                />
              </FormField>
              <FormField
                label="Description"
                errorText={
                  formik.touched.description && formik.errors.description
                }
              >
                <Input
                  value={formik.values.description}
                  placeholder="My MLPigeon model version description"
                  onChange={(e) =>
                    formik.setFieldValue('description', e.detail.value)
                  }
                  onBlur={() =>
                    formik.setTouched({ ...formik.touched, description: true })
                  }
                />
              </FormField>
            </SpaceBetween>
          </form>
        </div>
      )}
      {error && <Box color="text-status-error">{error.message}</Box>}
    </Modal>
  );
};
